/**
 * Factory to generate structured data for SEO
 */
export function createStructuredData ({ currentPage, lang, tFunc }) {
  const baseUrl = 'https://memodiez.com'
  const pagePath = currentPage ? `/${currentPage}` : ''
  const fullUrl = `${baseUrl}/${lang}${pagePath}`

  // Common metadata for all pages
  const commonFields = {
    '@context': 'https://schema.org',
    inLanguage: lang,
    url: fullUrl,
    author: { '@type': 'Person', name: 'tr4p0.dev' },
    creator: { '@type': 'Person', name: 'tr4p0.dev' },
    dateModified: '2026-01-28'
  }

  // Home (Game) - main application schema
  if (!currentPage) {
    return {
      ...commonFields,
      '@type': 'SoftwareApplication',
      name: 'Memodiez',
      description: tFunc('meta.jsonld.description'),
      softwareVersion: '1.2.5',
      operatingSystem: 'All',
      applicationCategory: 'GameApplication',
      applicationSubCategory: 'CasualGame',
      genre: ['Memory', 'Puzzle', 'Educational', 'Family', 'Social'],
      audience: {
        '@type': 'Audience',
        audienceType: tFunc('meta.jsonld.audienceType')
      },
      about: {
        '@type': 'Person',
        name: 'Diego Armando Maradona',
        sameAs: 'https://en.wikipedia.org/wiki/Diego_Maradona'
      },
      gamePlatform: ['Browser', 'Mobile', 'Desktop'],
      installUrl: baseUrl,
      screenshot: [
        `${baseUrl}/assets/screenshots/rich-snippet-mobile-1.webp`,
        `${baseUrl}/assets/screenshots/rich-snippet-mobile-2.webp`,
        `${baseUrl}/assets/screenshots/rich-snippet-desktop-1.webp`,
        `${baseUrl}/assets/screenshots/rich-snippet-desktop-2.webp`
      ],
      image: [
        `${baseUrl}/og-image.jpg`,
        `${baseUrl}/assets/logos/logo-square-1200.jpg`
      ],
      availableLanguage: ['es', 'gl', 'it', 'nap', 'ca', 'eu', 'pt', 'en', 'fr', 'de'],
      serviceArea: ['Argentina', 'Spain', 'Italy', 'UK'],
      accessibilityFeature: tFunc('meta.jsonld.accessibilityFeature'),
      accessibilityControl: tFunc('meta.jsonld.accessibilityControl'),
      datePublished: '2025-07-10',
      isAccessibleForFree: true,
      browserRequirements: tFunc('meta.jsonld.browserRequirements'),
      softwareRequirements: tFunc('meta.jsonld.softwareRequirements'),
      storageRequirements: '5MB',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    }
  }

  // Memorama game page
  if (currentPage === 'memorama') {
    return {
      ...commonFields,
      '@type': 'Game',
      name: tFunc('meta.pages.memorama.title') || 'Memotest | Memodiez',
      description: tFunc('meta.pages.memorama.description'),
      image: `${baseUrl}/games/memorama.webp`,
      genre: ['Memory', 'Educational', 'Family'],
      numberOfPlayers: {
        '@type': 'QuantitativeValue',
        minValue: 1,
        maxValue: 4
      },
      gamePlatform: ['Browser', 'Mobile', 'Desktop'],
      about: {
        '@type': 'Person',
        name: 'Diego Armando Maradona',
        sameAs: 'https://en.wikipedia.org/wiki/Diego_Maradona'
      },
      accessibilityFeature: tFunc('meta.jsonld.accessibilityFeature'),
      accessibilityControl: tFunc('meta.jsonld.accessibilityControl'),
      isAccessibleForFree: true
    }
  }

  // Puzzle game page
  if (currentPage === 'puzzle') {
    return {
      ...commonFields,
      '@type': 'Game',
      name: tFunc('meta.pages.puzzle.title') || 'Armando a Maradona | Memodiez',
      description: tFunc('meta.pages.puzzle.description'),
      image: `${baseUrl}/games/puzzle.webp`,
      genre: ['Puzzle', 'Educational', 'Family'],
      numberOfPlayers: {
        '@type': 'QuantitativeValue',
        value: 1
      },
      gamePlatform: ['Browser', 'Mobile', 'Desktop'],
      about: {
        '@type': 'Person',
        name: 'Diego Armando Maradona',
        sameAs: 'https://en.wikipedia.org/wiki/Diego_Maradona'
      },
      accessibilityFeature: tFunc('meta.jsonld.accessibilityFeature'),
      accessibilityControl: tFunc('meta.jsonld.accessibilityControl'),
      isAccessibleForFree: true
    }
  }

  // Page types mapping
  const pageTypeMap = {
    about: 'AboutPage',
    contact: 'ContactPage'
    // other static pages default to WebPage
  }

  // Page-specific rich fields for enhanced snippets
  const pageSpecificFields = {
    about: {
      mainEntity: {
        '@type': 'Organization',
        name: 'Memodiez Team',
        description: tFunc('meta.jsonld.teamDescription')
      },
      about: {
        '@type': 'Person',
        name: 'Diego Armando Maradona',
        sameAs: 'https://en.wikipedia.org/wiki/Diego_Maradona'
      }
    },
    contact: {
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'tr4p0.dev@gmail.com',
        contactType: tFunc('meta.jsonld.contactType')
      },
      about: {
        '@type': 'SoftwareApplication',
        name: 'Memodiez'
      }
    },
    support: {
      about: {
        '@type': 'SoftwareApplication',
        name: 'Memodiez'
      },
      potentialAction: {
        '@type': 'DonateAction',
        target: `${baseUrl}/${lang}/support`
      }
    },
    privacy: {
      about: {
        '@type': 'SoftwareApplication',
        name: 'Memodiez'
      }
    },
    terms: {
      about: {
        '@type': 'SoftwareApplication',
        name: 'Memodiez'
      }
    }
  }

  const schemaType = pageTypeMap[currentPage] || 'WebPage'

  return {
    ...commonFields,
    '@type': schemaType,
    name: tFunc(`meta.pages.${currentPage}.title`) || tFunc('meta.title'),
    description: tFunc(`meta.pages.${currentPage}.description`) || tFunc('meta.description'),
    image: `${baseUrl}/og-image.jpg`,
    ...(pageSpecificFields[currentPage] || {})
  }
}
